import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import React, { useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { getLocalizedText } from '../utils/textLocalization';
import zaruka from '../images/zaruka.jpg';
import {
  EditableContent,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
} from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const findLocalizedRecord = (contentList, contentKey, preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return null;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return null;
  }

  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    const record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      return record;
    }
  }

  return records[0] || null;
};

const getLocalizedTextValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  const record = findLocalizedRecord(contentList, contentKey, preferredLanguages);
  const value = record?.value;

  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  return fallback;
};


function App() {
  const locale = getCurrentLanguage();
  const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
  const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
  const editryReady = isEditryConfigured();
  const { overlayEnabled, adminSession } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);

  usePreloadPublicData({
    contentKeys: [
      'prodlouzena_zaruka_prodlouzena_zaruka_nadpis',
      'prodlouzena_zaruka_prodlouzena_zaruka_obrazek',
      'prodlouzena_zaruka_prodlouzena_zaruka_text',
      'prodlouzena_zaruka_prodlouzena_zaruka_cudlik',
    ],
    auto: editryReady,
  });
  const contentListResponse = usePublicContentList();

  const allContent = useMemo(
    () => contentListResponse.data?.data || [],
    [contentListResponse.data]
  );

  const data = useMemo(() => ({
    nadpis: getLocalizedTextValue(allContent, 'prodlouzena_zaruka_prodlouzena_zaruka_nadpis', 'Prodlouzena zaruka', preferredLanguages),
    obrazek: getLocalizedTextValue(allContent, 'prodlouzena_zaruka_prodlouzena_zaruka_obrazek', zaruka, preferredLanguages),
    text: getLocalizedTextValue(allContent, 'prodlouzena_zaruka_prodlouzena_zaruka_text', '', preferredLanguages),
    cudlik: getLocalizedTextValue(allContent, 'prodlouzena_zaruka_prodlouzena_zaruka_cudlik', '#', preferredLanguages),
  }), [allContent, preferredLanguages]);

  const loading = editryReady && contentListResponse.loading && !allContent.length;
  const bikesText = getLocalizedText('general.bikes_caps');

  const registerBike = getLocalizedText('warranty.register');

  const handleScroll = (e) => {
    const hash = e.currentTarget.hash.substring(1);
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    document.title = getLocalizedText('warranty.page_title');
  }, []);

  if (loading) return (
    <div className="page">

    </div>
  )

  return (
    <div className="page">
        <div className="cesta-novinky cesta-zaruka">
          <NavLink to="/#kola" onClick={handleScroll}><button><span>{bikesText}</span></button></NavLink>
          <button className="cesta-active">
            <span>
              {isEditModeActive ? (
                <EditableContent
                  contentKey="prodlouzena_zaruka_prodlouzena_zaruka_nadpis"
                  language={editryLanguage}
                  as="span"
                  fallback={data.nadpis}
                  showFallbackWhileLoading={false}
                />
              ) : (
                data.nadpis
              )}
            </span>
          </button>
        </div>

        <div className="zaruka-obsah">
            {isEditModeActive ? (
              <EditableContent
                contentKey="prodlouzena_zaruka_prodlouzena_zaruka_obrazek"
                language={editryLanguage}
                as="img"
                contentType="file"
                fallback={data.obrazek}
                showFallbackWhileLoading={false}
              />
            ) : (
              <img src={data.obrazek} alt={data.nadpis}/>
            )}
            <h1>
              {isEditModeActive ? (
                <EditableContent
                  contentKey="prodlouzena_zaruka_prodlouzena_zaruka_nadpis"
                  language={editryLanguage}
                  as="span"
                  fallback={data.nadpis}
                  showFallbackWhileLoading={false}
                />
              ) : (
                data.nadpis
              )}
            </h1>
            {isEditModeActive ? (
              <EditableContent
                contentKey="prodlouzena_zaruka_prodlouzena_zaruka_text"
                language={editryLanguage}
                as="div"
                contentType="html"
                fallback={data.text}
                showFallbackWhileLoading={false}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: data.text }} />
            )}
            <NavLink to={data.cudlik || '#'}>
              <button>
                <span>{registerBike}</span>
              </button>
            </NavLink>

            {isEditModeActive && (
              <div style={{ marginTop: 12 }}>
                <h3 className="barlow-semibold">URL tlacitka (cudlik)</h3>
                <EditableContent
                  contentKey="prodlouzena_zaruka_prodlouzena_zaruka_cudlik"
                  language={editryLanguage}
                  as="span"
                  fallback={data.cudlik}
                  showFallbackWhileLoading={false}
                />
              </div>
            )}
        </div>
    </div>
  );
}

export default App;
